import * as fabric from 'fabric';
import { createFabricObject } from './canvasUtils';
import { getPointsToSmoothPath } from '../../utils/drawing';
import { gaussianSmooth, createCatmullRomPath } from '../../utils/smoothPath';
import { Tool } from '../../types';

interface ProgressiveStroke {
  id: string;
  points: { x: number; y: number }[];
  pointTimestamps: number[]; // Timestamp for each point (normalized to stroke start)
  color: string;
  width: number;
  tool: string;
  startTimestamp: number; // When stroke_start was recorded
  endTimestamp: number; // When stroke_end was recorded
  firstRenderTime?: number; // When we first start rendering this stroke
  totalPoints: number;
  drawnPoints: number;
  fabricObject?: fabric.Object;
  isComplete?: boolean; // Whether all points have been drawn
}

export class ProgressiveStrokeProcessor {
  private canvas: fabric.Canvas;
  private progressiveStrokes: Map<string, ProgressiveStroke> = new Map();
  private strokeMap: Map<string, fabric.Object>;
  
  constructor(canvas: fabric.Canvas, strokeMap: Map<string, fabric.Object>) {
    this.canvas = canvas;
    this.strokeMap = strokeMap;
  }
  
  // Start a progressive stroke
  startProgressiveStroke(
    strokeId: string, 
    tool: string, 
    color: string, 
    width: number, 
    point: { x: number; y: number }, 
    timestamp: number
  ): void {
    this.progressiveStrokes.set(strokeId, {
      id: strokeId,
      points: [point],
      pointTimestamps: [timestamp],
      color,
      width,
      tool,
      startTimestamp: timestamp,
      endTimestamp: timestamp, // Will be updated by stroke_end
      totalPoints: 1,
      drawnPoints: 0,
      fabricObject: undefined
    });
  }
  
  // Add all points for a stroke (from stroke_end or add_stroke event)
  setStrokePoints(
    strokeId: string, 
    points: { x: number; y: number }[], 
    endTimestamp: number
  ): void {
    const stroke = this.progressiveStrokes.get(strokeId);
    if (!stroke) {
      // Create stroke data if it doesn't exist (for add_stroke events)
      const firstPoint = points[0];
      if (!firstPoint) return;
      
      // Calculate duration based on points (roughly 5-8ms per point)
      const duration = Math.min(1200, Math.max(200, points.length * 6));
      
      this.progressiveStrokes.set(strokeId, {
        id: strokeId,
        points: points,
        pointTimestamps: [], // No individual timestamps for old recordings
        color: '#000000', // Default, will be updated
        width: 2, // Default, will be updated
        tool: 'pen', // Default, will be updated
        startTimestamp: endTimestamp - duration,
        endTimestamp: endTimestamp,
        totalPoints: points.length,
        drawnPoints: 0,
        fabricObject: undefined
      });
    } else {
      // Update existing stroke with all points
      // Only update if we don't already have individual point timestamps
      if (stroke.pointTimestamps.length === 0) {
        stroke.points = points;
        stroke.totalPoints = points.length;
      }
      // Otherwise keep the points we already collected with timestamps
    }
  }
  
  // Update stroke rendering based on current playback time
  updateStroke(strokeId: string, currentTime: number): boolean {
    const stroke = this.progressiveStrokes.get(strokeId);
    if (!stroke) return false;
    
    // Don't start drawing until we reach the stroke's start time
    if (currentTime < stroke.startTimestamp) {
      return false;
    }
    
    // Use actual stroke time, not render time
    const strokeElapsed = currentTime - stroke.startTimestamp;
    
    // If we've passed the end time, show all points
    if (currentTime >= stroke.endTimestamp) {
      if (stroke.drawnPoints < stroke.totalPoints) {
        return this.renderStroke(stroke, stroke.totalPoints);
      }
      return false;
    }
    
    // Calculate progress based on stroke elapsed time
    const recordedDuration = stroke.endTimestamp - stroke.startTimestamp;
    const progress = Math.max(0, Math.min(1, strokeElapsed / recordedDuration));
    
    // Calculate how many points should be visible
    let targetPoints = 1;
    
    // If we have individual point timestamps, use them for accurate playback
    if (stroke.pointTimestamps.length > 0) {
      // Use stroke elapsed time directly
      targetPoints = 0;
      for (let i = 0; i < stroke.pointTimestamps.length; i++) {
        if (stroke.pointTimestamps[i] <= strokeElapsed) {
          targetPoints = i + 1;
        } else {
          break;
        }
      }
      targetPoints = Math.max(1, targetPoints);
    } else {
      // Fallback to progress-based calculation
      targetPoints = Math.max(1, Math.ceil(stroke.totalPoints * progress));
    }
    
    // Only log first stroke render
    if (stroke.drawnPoints === 0 && targetPoints > 0) {
    }
    
    // Only update if we need to show more points
    if (targetPoints > stroke.drawnPoints) {
      return this.renderStroke(stroke, targetPoints);
    }
    
    return false;
  }
  
  // Easing function for smoother animation
  private easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }
  
  // Interpolate points to simulate coalesced events (missing high-frequency input)
  private interpolatePoints(points: { x: number; y: number; pressure?: number; timestamp?: number }[]): { x: number; y: number; pressure?: number; timestamp?: number }[] {
    if (points.length < 2) return points;
    
    const interpolated: { x: number; y: number; pressure?: number; timestamp?: number }[] = [points[0]];
    
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      
      // Calculate distance between points
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // If points are far apart, add interpolated points (simulate missing coalesced events)
      if (distance > 4) { // Threshold for interpolation
        const steps = Math.min(Math.ceil(distance / 3), 5); // Max 5 interpolated points
        
        for (let j = 1; j < steps; j++) {
          const t = j / steps;
          
          interpolated.push({
            x: prev.x + dx * t,
            y: prev.y + dy * t,
            pressure: prev.pressure !== undefined && curr.pressure !== undefined 
              ? prev.pressure + (curr.pressure - prev.pressure) * t 
              : curr.pressure || prev.pressure,
            timestamp: prev.timestamp !== undefined && curr.timestamp !== undefined
              ? prev.timestamp + (curr.timestamp - prev.timestamp) * t
              : curr.timestamp || prev.timestamp
          });
        }
      }
      
      interpolated.push(curr);
    }
    
    return interpolated;
  }
  
  // Render stroke with specified number of points
  private renderStroke(stroke: ProgressiveStroke, pointCount: number): boolean {
    if (pointCount < 1) return false;
    
    // Remove existing fabric object
    if (stroke.fabricObject) {
      this.canvas.remove(stroke.fabricObject);
    }
    
    // Get visible points
    const visiblePoints = stroke.points.slice(0, pointCount);
    
    // Check if this is a shape tool
    const shapeTools = ['rectangle', 'circle', 'line', 'arrow', 'triangle', 'star', 'hexagon', 'pentagon', 'diamond', 'cube', 'pyramid', 'ellipse'];
    const isShapeTool = shapeTools.includes(stroke.tool);
    
    // For shape tools, only render when we have enough points
    if (isShapeTool) {
      // For shapes, we need minimum points to render properly
      const minPointsForShape: Record<string, number> = {
        'rectangle': 5,
        'circle': 3,
        'line': 2,
        'arrow': 2,
        'triangle': 4,
        'star': 11,
        'hexagon': 7,
        'pentagon': 6,
        'diamond': 5,
        'cube': 16,
        'cylinder': 24,
        'pyramid': 11,
        'ellipse': 3,
        'parallelogram': 5,
        'trapezoid': 5,
        'grid': 4,
        'coordinate': 4,
        'coordinateGrid': 8
      };
      
      const minPoints = minPointsForShape[stroke.tool] || 4;
      
      // If we don't have enough points yet, skip rendering
      if (visiblePoints.length < minPoints) {
        return false;
      }
      
      // For shapes, we should only render when we have all points
      // This prevents incorrect shape generation from partial points
      if (visiblePoints.length < stroke.totalPoints) {
        return false;
      }
      
      // For shapes, always use the shape-specific rendering
      const strokeData = {
        id: stroke.id,
        points: visiblePoints,
        color: stroke.color,
        width: stroke.width,
        tool: stroke.tool as Tool,
        timestamp: stroke.endTimestamp
      };
      
      
      // Try to find and convert the preview instead of creating new
      const preview = this.findShapePreview(stroke.id);
      
      if (preview) {
        
        // Remove the preview
        this.canvas.remove(preview);
        
        // Create final shape using the same properties but without dashed line
        let finalObject: fabric.Object | null = null;
        
        // Get common properties from preview
        const commonProps = {
          stroke: preview.stroke,
          strokeWidth: preview.strokeWidth,
          fill: preview.fill || 'transparent',
          strokeLineCap: 'round' as const,
          strokeLineJoin: 'round' as const,
          strokeDashArray: undefined, // Remove dashed line
          selectable: false,
          evented: false,
          opacity: 1, // Full opacity
          left: preview.left,
          top: preview.top,
          strokeId: stroke.id
        };
        
        // Convert based on preview type
        if (preview.type === 'ellipse') {
          const ellipsePreview = preview as fabric.Ellipse;
          finalObject = new fabric.Ellipse({
            ...commonProps,
            rx: ellipsePreview.rx,
            ry: ellipsePreview.ry
          });
        } else if (preview.type === 'rect') {
          const rectPreview = preview as fabric.Rect;
          finalObject = new fabric.Rect({
            ...commonProps,
            width: rectPreview.width,
            height: rectPreview.height
          });
        } else if (preview.type === 'circle') {
          const circlePreview = preview as fabric.Circle;
          finalObject = new fabric.Circle({
            ...commonProps,
            radius: circlePreview.radius
          });
        } else if (preview.type === 'path') {
          const pathPreview = preview as fabric.Path;
          // For path objects, we need to get the path data
          // Fabric.js stores path data as a parsed array, but we can get the original path string
          const pathString = (pathPreview as any).path ? 
            (pathPreview as any).path.map((cmd: any[]) => cmd.join(' ')).join(' ') :
            (pathPreview as any).__pathData || (pathPreview as any)._pathData || '';
          
          if (pathString) {
            finalObject = new fabric.Path(pathString, commonProps);
          } else {
          }
        }
        
        if (finalObject) {
          this.canvas.add(finalObject);
          stroke.fabricObject = finalObject;
          stroke.drawnPoints = pointCount;
          
          // Update stroke map when fully drawn
          if (pointCount >= stroke.totalPoints) {
            this.strokeMap.set(stroke.id, finalObject);
            stroke.isComplete = true;
          }
          
          return true;
        }
      }
      
      // Fallback to creating new object if preview not found or conversion failed
      this.removeShapePreview(stroke.id);
      
      const fabricObject = createFabricObject(strokeData);
      
      if (fabricObject) {
        this.canvas.add(fabricObject);
        stroke.fabricObject = fabricObject;
        stroke.drawnPoints = pointCount;
        
        // Update stroke map when fully drawn
        if (pointCount >= stroke.totalPoints) {
          this.strokeMap.set(stroke.id, fabricObject);
          stroke.isComplete = true;
        }
        
        return true;
      } else {
      }
    } else {
      // For freehand tools (pen, pencil, highlighter), use progressive rendering
      if (visiblePoints.length >= 2) {
        // Apply the SAME advanced smoothing as whiteboard for pen/pencil
        let pathData: string;
        
        if (stroke.tool === 'pen' || stroke.tool === 'pencil') {
          // For pen, interpolate points first to simulate coalesced events
          let processedPoints = visiblePoints;
          if (stroke.tool === 'pen') {
            processedPoints = this.interpolatePoints(visiblePoints);
          }
          
          // Apply Gaussian smoothing + Catmull-Rom splines (like whiteboard)
          const smoothedPoints = gaussianSmooth(processedPoints, 1.5);
          pathData = createCatmullRomPath(smoothedPoints, 0.5);
        } else {
          // Use standard smoothing for other tools
          pathData = getPointsToSmoothPath(visiblePoints);
        }
        
        // Create path object
        const path = new fabric.Path(pathData, {
          stroke: stroke.color,
          strokeWidth: stroke.width,
          fill: 'transparent',
          strokeLineCap: 'round' as const,
          strokeLineJoin: 'round' as const,
          selectable: false,
          evented: false
        });
        
        this.canvas.add(path);
        stroke.fabricObject = path;
        stroke.drawnPoints = pointCount;
        
        // Mark as complete when all points are drawn
        if (pointCount >= stroke.totalPoints) {
          this.strokeMap.set(stroke.id, path);
          stroke.isComplete = true;
        }
        
        return true;
      }
    }
    
    return false;
  }
  
  // Update all progressive strokes
  updateAll(currentTime: number): void {
    let needsRender = false;
    
    this.progressiveStrokes.forEach((stroke, strokeId) => {
      if (this.updateStroke(strokeId, currentTime)) {
        needsRender = true;
      }
    });
    
    if (needsRender) {
      this.canvas.requestRenderAll();
    }
  }
  
  // Set stroke properties (from add_stroke event)
  setStrokeProperties(
    strokeId: string, 
    color: string, 
    width: number, 
    tool: string,
    timestamp: number
  ): void {
    const stroke = this.progressiveStrokes.get(strokeId);
    if (stroke) {
      stroke.color = color;
      stroke.width = width;
      stroke.tool = tool;
      
      // Don't update timestamps here - they're already set in setStrokePoints
    } else {
      // If stroke doesn't exist, we might be dealing with an old recording
      // that only has add_stroke events
    }
  }
  
  // Set stroke end time from stroke_end event
  setStrokeEndTime(strokeId: string, endTimestamp: number): void {
    const stroke = this.progressiveStrokes.get(strokeId);
    if (stroke) {
      stroke.endTimestamp = endTimestamp;
      
      // Don't mark as complete here - wait for all points to be drawn
      // The stroke might still receive more points after stroke_end
    }
  }
  
  // Add a point with timestamp
  addPoint(strokeId: string, point: { x: number; y: number }, timestamp: number): void {
    const stroke = this.progressiveStrokes.get(strokeId);
    if (stroke) {
      stroke.points.push(point);
      // Normalize timestamp relative to stroke start
      const normalizedTimestamp = timestamp - stroke.startTimestamp;
      stroke.pointTimestamps.push(normalizedTimestamp);
      stroke.totalPoints = stroke.points.length;
      
      // If stroke was marked complete, unmark it since we have new points
      if (stroke.isComplete) {
        stroke.isComplete = false;
        stroke.drawnPoints = Math.min(stroke.drawnPoints, stroke.points.length - 1);
      }
      
    }
  }
  
  // Check if stroke exists
  hasStroke(strokeId: string): boolean {
    return this.progressiveStrokes.has(strokeId);
  }
  
  // Clear render times for backward seeking
  clearRenderTimes(): void {
    this.progressiveStrokes.forEach(stroke => {
      stroke.firstRenderTime = undefined;
      stroke.drawnPoints = 0;
      if (stroke.fabricObject) {
        this.canvas.remove(stroke.fabricObject);
        stroke.fabricObject = undefined;
      }
    });
  }
  
  // Reset processor
  reset(): void {
    // Clear firstRenderTime from all strokes before clearing
    this.progressiveStrokes.forEach(stroke => {
      stroke.firstRenderTime = undefined;
      if (stroke.fabricObject) {
        this.canvas.remove(stroke.fabricObject);
      }
    });
    this.progressiveStrokes.clear();
  }
  
  // Clean up old completed strokes
  cleanupCompletedStrokes(currentTime: number): void {
    const strokesToRemove: string[] = [];
    
    this.progressiveStrokes.forEach((stroke, strokeId) => {
      // Remove strokes that have been complete for more than 5 seconds
      if (stroke.isComplete && currentTime - stroke.endTimestamp > 5000) {
        strokesToRemove.push(strokeId);
      }
    });
    
    strokesToRemove.forEach(strokeId => {
      this.progressiveStrokes.delete(strokeId);
    });
  }
  
  // Find shape preview
  private findShapePreview(strokeId: string): fabric.Object | undefined {
    const objects = this.canvas.getObjects();
    return objects.find(obj => 
      (obj as any).strokeId === strokeId && 
      (obj as any).isPreview === true
    );
  }
  
  // Remove shape preview
  private removeShapePreview(strokeId: string): void {
    const preview = this.findShapePreview(strokeId);
    if (preview) {
      this.canvas.remove(preview);
    }
  }
}